/**
 * Bobo's speech bubble for React Native (port of the admin `BoboBubble`): white card, orange title, small arrow
 * pointing at Bobo. Announced politely to screen readers.
 *
 * @module components/bobo/BoboBubble
 */

import React, {useEffect} from 'react';
import {StyleSheet, Text, View} from 'react-native';
import Animated, {Easing, useAnimatedStyle, useReducedMotion, useSharedValue, withTiming} from 'react-native-reanimated';

/**
 * @param {Object} props Props.
 * @param {(string|null)} [props.title] Small orange title.
 * @param {string} props.text The message.
 * @param {string} [props.placement] 'side' (arrow points left at Bobo) or 'below' (arrow points up, Bobo is above).
 * @param {*} [props.style] Extra style.
 * @returns {JSX.Element} The bubble.
 */
const BoboBubble = ({title = null, text, placement = 'side', style}) => {
    const reduced = useReducedMotion();
    const pop = useSharedValue(reduced ? 1 : 0);

    useEffect(() => {
        if (!reduced) {
            pop.value = 0;
            pop.value = withTiming(1, {duration: 420, easing: Easing.out(Easing.back(1.6))});
        }
    }, [text, title, reduced, pop]);
    const animated = useAnimatedStyle(() => ({
        opacity: Math.min(1, pop.value * 1.4),
        transform: [{translateY: (1 - pop.value) * 6}, {scale: 0.96 + 0.04 * pop.value}],
    }));

    return (
        <Animated.View
            accessibilityRole="text"
            accessibilityLiveRegion="polite"
            style={[styles.bubble, placement === 'below' ? styles.below : styles.side, animated, style]}
        >
            <View style={[styles.arrow, placement === 'below' ? styles.arrowUp : styles.arrowLeft]} />
            {!!title && <Text style={styles.title}>{title}</Text>}
            <Text style={styles.text}>{text}</Text>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    bubble: {
        backgroundColor: '#FFFFFF',
        borderRadius: 18,
        borderWidth: 1,
        borderColor: '#FED7AA',
        paddingHorizontal: 15,
        paddingVertical: 12,
        shadowColor: '#7C2D12',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.16,
        shadowRadius: 16,
        elevation: 4,
    },
    side: {flex: 1, minWidth: 0},
    below: {alignSelf: 'stretch', marginTop: 10},
    arrow: {
        position: 'absolute',
        width: 14,
        height: 14,
        backgroundColor: '#FFFFFF',
        borderColor: '#FED7AA',
        borderRadius: 3,
        transform: [{rotate: '45deg'}],
    },
    arrowLeft: {left: -8, bottom: 20, borderLeftWidth: 1, borderBottomWidth: 1},
    arrowUp: {top: -8, alignSelf: 'center', left: '50%', marginLeft: -7, borderLeftWidth: 1, borderTopWidth: 1},
    title: {fontSize: 12.5, fontWeight: '800', color: '#EA580C', letterSpacing: 0.2, marginBottom: 2},
    text: {fontSize: 14, lineHeight: 20, color: '#1E293B', fontWeight: '500'},
});

export default BoboBubble;
